import { useEffect, useRef } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  useStore,
  useNodesInitialized,
  addEdge
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import Converter from "./converter.js"
import Connections from "./connections.js"
import ProcessorNode from "./processorNode.js"
import BhFlowEdge, { BhEdgeMarkers } from "../flowkit/flowEdge.js"

const FlowWrapper = ({shell, shown, ...rest}) => {
  const converter = new Converter(shell.manifest);
  const reactFlowInstance = useReactFlow();
  const flowRef = useRef(null);
  const hasFitRef = useRef(false);

  // The canvas is fitted ONCE, the first time it genuinely has a size, and never again: from then
  // on the viewport belongs to the user. All three workflow tabs stay mounted and merely hide with
  // `display: none`, so ReactFlow's own viewport already survives a tab switch — anything that
  // re-fits on the way back is throwing away where the user left the graph.
  //
  // Fitting cannot simply be deferred by a timer, because fitView does not measure the DOM: it
  // reads width/height from ReactFlow's store, fed by a ResizeObserver on the canvas. While this
  // tab is hidden the canvas measures zero, and useResizeHandler stores 500x500 rather than the
  // zero it saw (its `size.width || 500` fallback). A fit taken before that observer has reported
  // the real box therefore centres the graph for a 500x500 canvas — which on a larger one puts it
  // up in the top-left corner, exactly the reported symptom. So gate on the store having caught up
  // with the element's own offset box, which is the same measurement the observer feeds it.
  const canvasWidth = useStore((state) => state.width);
  const canvasHeight = useStore((state) => state.height);
  const nodesInitialized = useNodesInitialized();

  useEffect(() => {
    // `shown` is both the trigger — it is the event a reveal actually produces — and the statement
    // that being off screen, not being zero-sized, is what disqualifies a fit. Today those coincide
    // because the tab hides with `display: none`; `visibility: hidden` would leave a full-sized box.
    if ( hasFitRef.current || !shown || !nodesInitialized ) {
      return;
    }

    // Zero here means the canvas is not rendered, and it is not redundant with the comparison
    // below: ReactFlow's store also starts at 0x0, which would agree with an unrendered box and fit
    // the graph into nothing. A mismatch means the observer has not yet reported this size, and
    // fitView would still be working from the stale one.
    const canvas = flowRef.current;
    if ( !canvas || !canvas.offsetWidth || !canvas.offsetHeight ) {
      return;
    }
    if ( canvasWidth !== canvas.offsetWidth || canvasHeight !== canvas.offsetHeight ) {
      return;
    }

    hasFitRef.current = true;
    reactFlowInstance.fitView({ padding: 0.1 });
  }, [shown, nodesInitialized, canvasWidth, canvasHeight, reactFlowInstance]);

  const nodeTypes = {
    processorNode: ProcessorNode,
  };

  const edgeTypes = {
    bhEdge: BhFlowEdge,
  };

  const onNodesChange = (changes) => {
    // Mark workflow as modified if impacted
    const isModified = converter.doReactFlowChangesImpactDefinition(changes);

    // Ignore changes occurring
    // - revision is not editable
    // - while the component is hidden
    if ( isModified && ( !shell.shellInterface.canEditRevision() || !shown ) ) {
      return;
    }

    // Apply changes
    shell.workflowInterface.setNodes(applyNodeChanges(changes, shell.nodes), isModified);
  };

  const onEdgesChange = (changes) => {
    // Mark workflow as modified if impacted
    const isModified = converter.doReactFlowChangesImpactDefinition(changes);

    // Ignore changes occurring
    // - revision is not editable
    // - while the component is hidden
    if ( isModified && ( !shell.shellInterface.canEditRevision() || !shown ) ) {
      return;
    }

    // Apply changes
    shell.workflowInterface.setEdges(applyEdgeChanges(changes, shell.edges), isModified);
  };

  const onConnect = (connection) => {
    // Ignore changes occurring
    // - revision is not editable
    if ( !shell.shellInterface.canEditRevision() ) {
      return;
    }

    // Confirm compatibility of connection ports
    if ( !Connections.checkConnectionPortsCompatibility(connection) ) {
      return;
    }

    // Workflow/processor limits are not enforced in here to enable free-form flow editing with minimal restrictions.
    // All constraints are verified and enforced on the backend in the routine preceding workflow execution.

    // Prepare edge object
    converter.completeNewEdgeDefinition(shell.nodes, shell.edges, connection);

    // Create edge based on connection
    shell.workflowInterface.setEdges(addEdge(connection, shell.edges), true);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const { screenToFlowPosition } = reactFlowInstance;

  const onDrop = (event) => {
    let processorDefinition = {};

    try {
      // Retrieve processor definition from the event
      processorDefinition = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    // Prevent default action
    event.preventDefault();

    // Determine position of the dropped element
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    // Create new node
    const node = converter.createNewNode(processorDefinition, position, shell.nodes);

    // Add node to the global list
    shell.workflowInterface.setNodes(shell.nodes.concat(node), true);
  };

  const onSelectionChange = (params) => {
    if ( 1 == params.nodes.length && 0 == params.edges.length ) {
      shell.shellInterface.updateDetails("processor", params.nodes[0]);
    } else if ( 0 == params.nodes.length && 1 == params.edges.length ) {
      shell.shellInterface.updateDetails("connection", params.edges[0]);
    } else {
      shell.shellInterface.updateDetails(null, null);
    }
  }

  return (
    <ReactFlow
      ref={flowRef}
      nodes={shell.nodes}
      edges={shell.edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onSelectionChange={onSelectionChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      attributionPosition="bottom-right"
      {...rest}
    >
      <BhEdgeMarkers />
      {!shell.progress &&
        <Controls
          showInteractive={false}
        />
      }
      <Background id="workflow-background" variant={BackgroundVariant.Dots} gap={12} size={1} />
    </ReactFlow>
  )
}

export default FlowWrapper;
